import { PayloadAction, createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GetVariableNameBatteryListQuery,
  GetAllVariablesQuery,
  IVariablesClient,
  VariableTypes,
  VariablesClient,
  GetAllDatasetVariablesQuery,
} from "app/ApiClient";
import { IState } from "slices";
import { LoadingState } from "types";
import { initialVariablesState } from "./variablesState";

const variablesClient: IVariablesClient = new VariablesClient(process.env.REACT_APP_API_URL);
export const getremovedUnexpectedString = (variableFullName: string): string => {
  return variableFullName.replaceAll(/\[{.*?}]/g, "");
};
export const getVariables = createAsyncThunk(
  "variables/getVariables",
  async (
    request: {
      projectId: number;
      encodedVersionId: string;
      modificationId: number | undefined;
      stackId: number | undefined;
      requestedType?: VariableTypes;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const { pageContext } = (getState() as IState).projectsData;
      const query: GetAllVariablesQuery = {
        ...request,
        batteries: pageContext.filter?.batteries,
      };
      const result = await variablesClient.getAll(query);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getDatasetVariables = createAsyncThunk(
  "variables/getDatasetVariables",
  async (
    request: {
      projectId: number;
      encodedVersionId: string;
      modificationId: number | undefined;
      stackId: number | undefined;
      requestedType?: VariableTypes;
    },
    { rejectWithValue },
  ) => {
    try {
      const query: GetAllDatasetVariablesQuery = {
        ...request,
      };
      const result = await variablesClient.getAllDatasetVariables(query);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getVariableBatteryList = createAsyncThunk(
  "variables/getVariableBatteryList",
  async (
    request: {
      projectId: number;
      encodedVersionId: string;
      modificationId: number | undefined;
      stackId: number | undefined;
      requestedType?: VariableTypes;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const { pageContext } = (getState() as IState).projectsData;
      const query: GetVariableNameBatteryListQuery = {
        ...request,
        batteries: pageContext.filter?.batteries,
      };
      const result = await variablesClient.getVariableNameBattery(query);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getDatasetVariableBatteryList = createAsyncThunk(
  "variables/getDatasetVariableBatteryList",
  async (
    request: {
      projectId: number;
      encodedVersionId: string;
      modificationId: number | undefined;
      stackId: number | undefined;
      requestedType?: VariableTypes; 
    },
    { rejectWithValue },
  ) => {
    try {
      return await variablesClient.getDatasetVariableNameBattery(request);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getDatasetVariableBatteryListWithoutFilter = createAsyncThunk(
  "variables/getDatasetVariableBatteryListWithoutFilter",
  async (
    request: {
      projectId: number;
      encodedVersionId: string;
      modificationId: number | undefined;
      stackId: number | undefined;
      requestedType?: VariableTypes; 

    },
    { rejectWithValue },
  ) => {
    try {
      return await variablesClient.getDatasetVariableNameBattery({...request, applyFilter: false});
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllDatasetVariables = createAsyncThunk(
  "variables/getAllDatasetVariables",
  async (
    request: {
      projectId: number;
      encodedVersionId: string;
      modificationId: number | undefined;
      stackId: number | undefined;
      requestedType?: VariableTypes;
    },
    { rejectWithValue },
  ) => {
    try {
      return await variablesClient.getDatasetVariableNameBattery(request);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getVariableCategories = createAsyncThunk(
  "variables/getVariableCategories",
  async (
    {
      projectId,
      versionId,
      variable,
      stackId,
      modificationId,
    }: {
      projectId: number;
      versionId: string;
      variable: string;
      stackId: number | undefined;
      modificationId: number | undefined;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await variablesClient.getCategories(projectId, versionId, variable, modificationId, stackId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loadVariableCategories = createAsyncThunk(
  "variables/loadVariableCategories",
  async (
    {
      projectId,
      versionId,
      variable,
      stackId,
      modificationId,
    }: {
      projectId: number;
      versionId: string;
      variable: string;
      stackId: number | undefined;
      modificationId: number | undefined;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await variablesClient.getCategories(projectId, versionId, variable, modificationId, stackId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCategoriesForVariableList = createAsyncThunk(
  "variables/getCategoriesForVariableList",
  async (
    {
      projectId,
      versionId,
      variableList,
      stackId,
      modificationId,
    }: {
      projectId: number;
      versionId: string;
      variableList: Array<string>;
      stackId?: number;
      modificationId?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await variablesClient.getCategoriesForVariables(
        projectId,
        versionId,
        modificationId,
        stackId,
        variableList,
      );
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const resetVariableCategories = createAction<string>("variables/resetVariableCategories");
const variablesSlice = createSlice({
  name: "variables",
  initialState: initialVariablesState,
  reducers: {
    resetVariables: () => initialVariablesState,
    resetCategories: (state) => {
      state.categories = [];
      state.categoriesLoading = LoadingState.idle;
    },
    resetBulkCategories: (state) => {
      state.bulkCategoriesForVariables = [];
      state.bulkCategoriesLoading = LoadingState.idle;
    },
    updateVariableMetaData:(state, {payload}: PayloadAction<string>)=>{
        if(!state.variableMetaData[payload]) {
          state.variableMetaData[payload] = [];
        }
    }
  },
  extraReducers: (builder) => {
    // getVariables
    builder.addCase(getVariables.pending, (state, { meta }) => {
      if (meta.arg.requestedType === VariableTypes.Scalar) {
        state.numericVariable = [];
        state.numericVariableLoading = LoadingState.pending;
      } else {
        state.variables = [];
        state.loading = LoadingState.pending;
      }
    });
    builder.addCase(getVariables.fulfilled, (state, { payload, meta }) => {
      if (meta.arg.requestedType === VariableTypes.Scalar) {
        state.numericVariable = payload || [];
        state.numericVariableLoading = LoadingState.succeed;
      } else {
        state.variables = payload || [];
        state.loading = LoadingState.succeed;
      }
    });
    builder.addCase(getVariables.rejected, (state, { meta }) => {
      if (meta.arg.requestedType === VariableTypes.Scalar) {
        state.numericVariableLoading = LoadingState.failed;
      } else {
        state.loading = LoadingState.failed;
      }
    });
    // getVariableBatteryList
    builder.addCase(getVariableBatteryList.pending, (state) => {
      state.variableNameBatteryList = [];
      state.loading = LoadingState.pending;
    });
    builder.addCase(getVariableBatteryList.fulfilled, (state, { payload }) => {
      state.variableNameBatteryList = payload;
      state.loading = LoadingState.succeed;
    });
    builder.addCase(getVariableBatteryList.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
    // getDatasetVariableBatteryList
    builder.addCase(getDatasetVariableBatteryList.pending, (state) => {
      state.variableNameBatteryList = [];
      state.loading = LoadingState.pending;
    });
    builder.addCase(getDatasetVariableBatteryList.fulfilled, (state, { payload }) => {
      state.variableNameBatteryList = payload;
      state.loading = LoadingState.succeed;
    });
    builder.addCase(getDatasetVariableBatteryList.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
    // getVariableCategories
    builder.addCase(getVariableCategories.pending, (state) => {
      state.categoriesLoading = LoadingState.pending;
      state.categories = [];
    });
    builder.addCase(getVariableCategories.fulfilled, (state, { payload }) => {
      state.categoriesLoading = LoadingState.succeed;
      state.categories = payload;
    });
    builder.addCase(getVariableCategories.rejected, (state) => {
      state.categoriesLoading = LoadingState.failed;
    });

    builder.addCase(loadVariableCategories.pending, (state, { meta }) => {
      state.variableCategoriesLoadingStatus = LoadingState.pending;
      const { arg } = {...meta};
      if (!state.variableMetaData[arg.variable]) {
        state.variableMetaData[arg.variable] = [];
      }
    });

    builder.addCase(
      loadVariableCategories.fulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: { variable },
          },
        },
      ) => {
        state.variableCategoriesLoadingStatus = LoadingState.succeed;
        state.variableMetaData[variable] =  [...payload];
        state.variableCategories[`${variable}`] = payload;
      },
    );

    builder.addCase(
      loadVariableCategories.rejected,
      (
        state,
        {
          meta: {
            arg: { variable },
          },
        },
      ) => {
        state.variableCategoriesLoadingStatus = LoadingState.failed;
        delete state.variableCategories[`${variable}`];
      },
    );

    builder.addCase(resetVariableCategories, (state, { payload }) => {
      delete state.variableCategories[`${payload}`];
    });

    builder.addCase(getCategoriesForVariableList.pending, (state) => {
      state.bulkCategoriesForVariables = [];
      state.bulkCategoriesLoading = LoadingState.pending;
    });

    builder.addCase(getCategoriesForVariableList.fulfilled, (state, { payload }) => {
      state.bulkCategoriesForVariables = payload;
      state.bulkCategoriesLoading = LoadingState.succeed;
    });

    builder.addCase(getCategoriesForVariableList.rejected, (state) => {
      state.bulkCategoriesForVariables = [];
      state.bulkCategoriesLoading = LoadingState.failed;
    });

    // getDatasetVariables
    builder.addCase(getDatasetVariables.pending, (state, { meta }) => {
      if (meta.arg.requestedType === VariableTypes.Scalar) {
        state.numericVariable = [];
        state.numericVariableLoading = LoadingState.pending;
      } else {
        state.variables = [];
        state.loading = LoadingState.pending;
      }
    });
    builder.addCase(getDatasetVariables.fulfilled, (state, { payload, meta }) => {
      if (meta.arg.requestedType === VariableTypes.Scalar) {
        state.numericVariable = payload || [];
        state.numericVariableLoading = LoadingState.succeed;
      } else {
        state.variables = payload || [];
        state.loading = LoadingState.succeed;
      }
    });
    builder.addCase(getDatasetVariables.rejected, (state, { meta }) => {
      if (meta.arg.requestedType === VariableTypes.Scalar) {
        state.numericVariableLoading = LoadingState.failed;
      } else {
        state.loading = LoadingState.failed;
      }
    });
    // getDatasetVariableBatteryList
    builder.addCase(getAllDatasetVariables.pending, (state) => {
      state.allVariableNameBatteryList = [];
    });
    builder.addCase(getAllDatasetVariables.fulfilled, (state, { payload }) => {
      state.allVariableNameBatteryList = payload;
    });
    builder.addCase(getAllDatasetVariables.rejected, (state) => {
      state.allVariableNameBatteryList = [];
    });
    
    builder.addCase(getDatasetVariableBatteryListWithoutFilter.pending, (state) => {
      state.unfilteredVariableNameBatteryList = [];
    });
    builder.addCase(getDatasetVariableBatteryListWithoutFilter.fulfilled, (state, { payload }) => {
      state.unfilteredVariableNameBatteryList = payload;
    });
    builder.addCase(getDatasetVariableBatteryListWithoutFilter.rejected, (state) => {
      state.unfilteredVariableNameBatteryList = [];
    });
  },
});
export const { resetCategories, resetVariables, resetBulkCategories, updateVariableMetaData } = variablesSlice.actions;
export const variablesSelector = (state: IState) => state.variables;
export default variablesSlice.reducer;
